import * as React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import Loader from "../../components/general/loader";
import { Title } from "../general/typography";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { getValidOverrides } from "../../scripts/api_v2/overrides_client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { formatDate, fadeOverride, clearableOverride } from "./utils";
import dayjs from "dayjs";
import { Table, TableBody, TableHead, TableRow, TableCell } from "@mui/material";
import { asKw } from "../../scripts/utils";
import { OVERRIDE_TYPES, getModeLabelByValue, OVERRIDE_MODES } from "../../enums/override_enum";
import { clearOverride } from "../../scripts/api_v2/overrides_client";
import { useSnackbar } from "notistack";

export function CurrentOverrides({ siteData }) {
  const theme = useTheme();
  const backend = useBackend();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const clearingRef = React.useRef(null);

  const {
    data: overrides,
    error,
    isLoading,
    refetch: refetchOverrides,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.CURRENT_OVERRIDES, backend.fetch, siteData.site_id],
    queryFn: () => getValidOverrides(backend.fetch, siteData.site_id),
    refetchInterval: 30000,
  });

  const { mutate: handleClearOverride, isPending: isClearing } = useMutation({
    mutationFn: async (override) => {
      const f = await clearOverride(backend.fetch, siteData.site_id, override.id);
      if (f.status !== 200) {
        console.error(f);
        throw new Error("Override clear failed");
      }
    },
    onMutate: async (override) => {
      clearingRef.current = override.id;
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.CURRENT_OVERRIDES, backend.fetch, siteData.site_id],
      });
      // Ideally we would optimistically update to the new value
    },
    onError: (err, variables, context) => {
      clearingRef.current = 0;
      console.error(err);
      enqueueSnackbar("Override clear failed", { variant: "error" });
    },
    onSuccess: async () => {
      clearingRef.current = null;
      await refetchOverrides();
      enqueueSnackbar("Override cleared!", { variant: "success" });
    },
  });

  function Value({ data }) {
    if (data.type === OVERRIDE_TYPES.FRACTION.value) {
      return <Typography style={{ fontWeight: "bold" }}>{data.value * 100}%</Typography>;
    } else {
      return <Typography style={{ fontWeight: "bold" }}>{asKw(data.value)} kW</Typography>;
    }
  }

  function deviceName(device_id) {
    if (!device_id) return "All";
    else if (!siteData.devices) return "Unknown";
    else if (!siteData.devices[device_id]) return "Unkown";
    else return siteData.devices[device_id].name;
  }

  function OverrideRow({ data, theme }) {
    const fadeOut = fadeOverride(data);
    const clearable = clearableOverride(data);
    return (
      <TableRow
        style={{
          backgroundColor: fadeOut ? theme.palette.grey[200] : "transparent", // Light grey background for disabled rows
          opacity: fadeOut && !clearable ? 0.5 : 1, // Reduced opacity for disabled rows
        }}
      >
        <TableCell>
          <Typography style={{ fontWeight: "bold" }}>{formatDate(data.valid_from_utc, data.valid_to_utc)}</Typography>
        </TableCell>
        <TableCell>
          <Typography style={{ fontWeight: "bold" }}>{deviceName(data.device_id)}</Typography>
          <Typography>{data.device_id ? `[${data.device_id}]` : ""}</Typography>
        </TableCell>
        <TableCell>
          <Typography style={{ fontWeight: "bold" }}>{getModeLabelByValue(data.override)}</Typography>
        </TableCell>
        <TableCell>
          <Value data={data} />
        </TableCell>
        <TableCell>
          <Typography style={{ fontWeight: "bold" }}>{data.creator}</Typography>
          <Typography>on {data.source.toLowerCase()}</Typography>
        </TableCell>
        <TableCell align="center">
          {isClearing && clearingRef.current === data.id ? (
            <Loader />
          ) : (
            <Button onClick={() => handleClearOverride(data)} disabled={!clearable || isClearing} color="error">
              {data.enabled ? "Clear" : "Cleared"}
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }

  const TableHeader = () => (
    <TableHead style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: theme.palette.background.default }}>
      <TableRow>
        <TableCell>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Validity Period
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Device ID
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Override Type
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Value
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Issued by
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Clear
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const Overrides = ({ rows, theme }) => (
    <Box style={{ maxHeight: "300px", overflowY: "auto" }}>
      <Table size="small">
        <TableHeader theme={theme} />
        <TableBody>
          {rows.map((row, index) => (
            <OverrideRow key={index} data={row} theme={theme} />
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const RenderOverrides = () => {
    if (isLoading) {
      return <Loader sx={{ mb: 2 }} />;
    } else if (error) {
      return (
        <Typography variant="body" sx={{ ml: 6 }}>
          Error loading overrides;
        </Typography>
      );
    } else if (overrides.length === 0) {
      return (
        <Typography variant="body" sx={{ ml: 6 }}>
          No active overrides
        </Typography>
      );
    } else {
      return <Overrides rows={overrides} theme={theme} />;
    }
  };

  const boxSx = {
    width: "100%",
    borderRadius: 3,
    padding: 1,
    backgroundColor: "white",
  };

  return (
    <Box sx={boxSx}>
      <Title mb={2}>Current Overrides</Title>

      {RenderOverrides()}
    </Box>
  );
}
